import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Upload,
  FolderOpen,
  Loader2,
  Trash2,
  FileDown,
  Copy,
  ExternalLink,
  Search,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  certifyApi,
  parseUploadBlobIdFromFileUrl,
  getDisplayMessage,
  getApiUrl,
  getToken,
} from '@/api/apiClient';

const TIPO_OPTIONS = [
  { value: 'arquivo', label: 'Arquivo / anexo' },
  { value: 'email', label: 'E-mail (exportação futura)' },
  { value: 'web', label: 'Captura web (roadmap)' },
  { value: 'outro', label: 'Outro' },
];

function fileQueueKey(file) {
  return `${file.name}\0${file.size}\0${file.lastModified}`;
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes) || bytes <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i += 1;
  }
  const decimals = i === 0 ? 0 : 2;
  return `${n.toFixed(decimals)} ${u[i]}`;
}

export default function AuctionCertify() {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [selando, setSelando] = useState(false);
  /** Barra de estado durante “Selar evidência”: percent 0–100, indeterminate durante fases sem bytes. */
  const [sealProgress, setSealProgress] = useState(null);
  /** Fila local antes da selagem: um registo por ficheiro após “Selar”. */
  const [pendingFiles, setPendingFiles] = useState([]);
  const [form, setForm] = useState({
    titulo: '',
    tipo: 'arquivo',
    descricao: '',
    source_url: '',
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState('');
  const [pdfLoadingId, setPdfLoadingId] = useState('');

  const { data: listData, isLoading } = useQuery({
    queryKey: ['certify-evidences', busca],
    queryFn: () => certifyApi.evidencesList(busca),
  });

  const items = listData?.items ?? [];

  const { data: detailData } = useQuery({
    queryKey: ['certify-evidence', selectedId],
    queryFn: () => certifyApi.evidencesGet(selectedId),
    enabled: !!selectedId && detailOpen,
  });

  function addFilesToQueue(fileList) {
    const arr = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (arr.length === 0) {
      toast.message('Nenhum ficheiro válido seleccionado.');
      return;
    }
    let added = 0;
    setPendingFiles((prev) => {
      const seen = new Set(prev.map((p) => fileQueueKey(p.file)));
      const next = [...prev];
      for (const file of arr) {
        const k = fileQueueKey(file);
        if (seen.has(k)) continue;
        seen.add(k);
        next.push({ id: `${k}-${crypto.randomUUID()}`, file });
        added += 1;
      }
      return next;
    });
    if (added > 0) {
      toast.success(`${added} ficheiro(s) na fila (${arr.length} seleccionados, duplicados ignorados).`);
    } else {
      toast.message('Todos os ficheiros já estavam na fila.');
    }
  }

  function onPickMultipleFiles(e) {
    addFilesToQueue(e.target.files);
    e.target.value = '';
  }

  function onPickFolder(e) {
    addFilesToQueue(e.target.files);
    e.target.value = '';
  }

  function removePendingEntry(entryId) {
    setPendingFiles((prev) => prev.filter((p) => p.id !== entryId));
  }

  function clearPendingQueue() {
    setPendingFiles([]);
  }

  async function onSelar(e) {
    e.preventDefault();
    if (pendingFiles.length === 0) {
      toast.error('Adicione pelo menos um ficheiro (vários ficheiros ou uma pasta).');
      return;
    }
    if (!form.titulo.trim()) {
      toast.error('Informe o título.');
      return;
    }
    setSelando(true);
    const fila = pendingFiles.map((p) => p);
    const total = fila.length;
    const seg = 100 / total;
    setSealProgress({ percent: 0, title: 'A preparar envio…', sub: null, indeterminate: true });
    const baseTitulo = form.titulo.trim();
    let ok = 0;
    let fail = 0;
    try {
      for (let idx = 0; idx < fila.length; idx += 1) {
        const { file } = fila[idx];
        const shortName = file.webkitRelativePath || file.name;
        try {
          setSealProgress({
            percent: idx * seg,
            title: `A enviar ficheiro ${idx + 1} de ${total}`,
            sub: shortName,
            indeterminate: false,
          });
          const data = await certifyApi.uploadBlob(file, {
            onProgress: ({ loaded, total: totalBytes }) => {
              const denom = totalBytes > 0 ? totalBytes : file.size || 1;
              const ratio = Math.min(1, loaded / denom);
              const pct = Math.min(99.2, idx * seg + ratio * seg * 0.88);
              setSealProgress({
                percent: pct,
                title: `A enviar ficheiro ${idx + 1} de ${total}`,
                sub: `${shortName} · ${formatFileSize(loaded)} / ${formatFileSize(denom)}`,
                indeterminate: false,
              });
            },
          });
          if (data?.deduplicated) {
            toast.message(
              'Este ficheiro é byte-a-byte idêntico a um envio anterior: o SHA-256 é o mesmo e o armazenamento existente foi reutilizado (deduplicação).'
            );
          }
          const uploadBlobId =
            typeof data.upload_blob_id === 'string'
              ? data.upload_blob_id
              : parseUploadBlobIdFromFileUrl(data.file_url);
          if (!uploadBlobId) throw new Error('Resposta de upload sem identificador do arquivo.');
          const titulo = total === 1 ? baseTitulo : `${baseTitulo} — ${file.name}`;
          setSealProgress({
            percent: Math.min(99.5, idx * seg + seg * 0.92),
            title: 'A selar no servidor (hash e registo)…',
            sub: shortName,
            indeterminate: true,
          });
          await certifyApi.evidencesCreate({
            upload_blob_id: uploadBlobId,
            titulo,
            tipo: form.tipo,
            descricao: form.descricao.trim() || undefined,
            source_url: form.source_url.trim() || undefined,
          });
          setSealProgress({
            percent: (idx + 1) * seg,
            title: `Ficheiro ${idx + 1} de ${total} concluído`,
            sub: shortName,
            indeterminate: false,
          });
          ok += 1;
        } catch (err) {
          fail += 1;
          toast.error(`${file.name}: ${getDisplayMessage(err, 'Erro ao enviar ou selar')}`);
        }
      }
      if (ok > 0) {
        toast.success(`${ok} evidência(s) selada(s).${fail > 0 ? ` ${fail} com falha.` : ''}`);
        setForm((f) => ({ ...f, titulo: '', descricao: '', source_url: '' }));
        setPendingFiles([]);
        await queryClient.invalidateQueries({ queryKey: ['certify-evidences'] });
      } else if (fail > 0) {
        toast.error('Nenhuma evidência foi selada. Corrija os erros e tente novamente.');
      }
    } finally {
      setSealProgress(null);
      setSelando(false);
    }
  }

  function openDetail(id) {
    setSelectedId(id);
    setDetailOpen(true);
  }

  async function downloadPdf(id) {
    setPdfLoadingId(id);
    try {
      const token = getToken();
      const url = certifyApi.evidencesPdfRequestUrl(id);
      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || res.statusText);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition');
      let name = `AuctionCertify_${id.slice(0, 8)}.pdf`;
      if (cd) {
        const m = cd.match(/filename="([^"]+)"/i);
        if (m) name = m[1];
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Falha ao baixar PDF'));
    } finally {
      setPdfLoadingId('');
    }
  }

  async function onDelete(id) {
    if (!window.confirm('Excluir este registro? A verificação pública deixará de funcionar.')) return;
    setDeletingId(id);
    try {
      await certifyApi.evidencesDelete(id);
      toast.success('Registro excluído.');
      await queryClient.invalidateQueries({ queryKey: ['certify-evidences'] });
      if (selectedId === id) {
        setDetailOpen(false);
        setSelectedId(null);
      }
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Não foi possível excluir'));
    } finally {
      setDeletingId('');
    }
  }

  const verifyUrlFor = (id) =>
    typeof window !== 'undefined' ? `${window.location.origin}/verify/${id}` : `/verify/${id}`;

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <ShieldCheck className="h-8 w-8" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AuctionCertify</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-4xl">
            Selagem de evidências digitais com SHA-256, relatório em PDF e verificação pública protegida pelos 4 primeiros caracteres hex do hash. O SHA-256 resume o conteúdo binário: o mesmo ficheiro (mesmos bytes) gera sempre o mesmo hash; ficheiros diferentes produzem hashes diferentes.
            Os ficheiros grandes do Certify ficam em <strong>disco no servidor</strong> (pasta configurada por <code className="text-xs">UPLOAD_DIR</code>), com metadados na base. O teto por ficheiro segue o da API (por omissão até cerca de <strong>1 GB</strong>, conforme <code className="text-xs">CERTIFY_MAX_UPLOAD_BYTES</code>); em Docker use volume montado em <code className="text-xs">UPLOAD_DIR</code> para não perder dados ao recriar o contentor. Noutras áreas o limite de anexos é menor.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Novo registro</CardTitle>
          <CardDescription>
            Pode seleccionar vários ficheiros ou uma pasta (todos os ficheiros da pasta; suportado em Chrome, Edge e Safari). Cada ficheiro gera um registo selado; com vários, o título base repete-se com o nome do ficheiro.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              className="hidden"
              id="certify-files-input"
              multiple
              onChange={onPickMultipleFiles}
            />
            <input
              type="file"
              className="hidden"
              id="certify-folder-input"
              multiple
              {...{ webkitdirectory: '' }}
              onChange={onPickFolder}
            />
            <Button
              type="button"
              variant="outline"
              disabled={selando}
              onClick={() => document.getElementById('certify-files-input')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Escolher ficheiros
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={selando}
              onClick={() => document.getElementById('certify-folder-input')?.click()}
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Escolher pasta
            </Button>
            {pendingFiles.length > 0 && (
              <Button type="button" variant="ghost" size="sm" disabled={selando} onClick={clearPendingQueue}>
                Limpar fila
              </Button>
            )}
          </div>
          {pendingFiles.length > 0 ? (
            <ul className="max-h-48 overflow-y-auto rounded-md border text-sm divide-y">
              {pendingFiles.map(({ id, file }) => (
                <li key={id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate min-w-0" title={file.webkitRelativePath || file.name}>
                    {file.webkitRelativePath || file.name}
                    <span className="text-muted-foreground ml-2">
                      ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    disabled={selando}
                    onClick={() => removePendingEntry(id)}
                    aria-label="Remover da fila"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum ficheiro na fila.</p>
          )}
          {sealProgress ? (
            <div
              className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 space-y-2"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 gap-y-1">
                <p className="text-sm font-medium text-foreground">{sealProgress.title}</p>
                {!sealProgress.indeterminate ? (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {Math.round(Math.min(100, sealProgress.percent))}%
                  </span>
                ) : null}
              </div>
              {sealProgress.sub ? (
                <p className="text-xs text-muted-foreground truncate" title={sealProgress.sub}>
                  {sealProgress.sub}
                </p>
              ) : null}
              {sealProgress.indeterminate ? (
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-primary/20">
                  <div className="h-full w-full rounded-full bg-primary/45 animate-pulse" />
                </div>
              ) : (
                <Progress value={Math.min(100, Math.max(0, sealProgress.percent))} className="h-2.5" />
              )}
            </div>
          ) : null}
          <form onSubmit={onSelar} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="cert-titulo">Título</Label>
                <Input
                  id="cert-titulo"
                  value={form.titulo}
                  onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex.: Prints conversa XYZ"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  value={form.tipo}
                  onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                >
                  {TIPO_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cert-url">URL de origem (opcional)</Label>
                <Input
                  id="cert-url"
                  value={form.source_url}
                  onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value }))}
                  placeholder="https://…"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="cert-desc">Descrição (opcional)</Label>
                <Textarea
                  id="cert-desc"
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <Button type="submit" disabled={selando || pendingFiles.length === 0}>
              {selando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {pendingFiles.length <= 1 ? 'Selar evidência' : `Selar ${pendingFiles.length} evidências`}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Meus registros</CardTitle>
            <CardDescription>Busca pelo título. Link público: /verify/[id]</CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Filtrar…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma evidência ainda.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {items.map((row) => (
                <li key={row.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{row.titulo}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.tipo} · selado em{' '}
                      {row.sealed_at_pt_br ??
                        new Date(row.sealed_at).toLocaleString('pt-BR', { hour12: false })}
                    </p>
                    {row.public_password ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-950/80">
                          Senha de verificação
                        </span>
                        <code className="text-lg font-bold tracking-[0.25em] rounded-md border-2 border-amber-600 bg-amber-100 px-3 py-1.5 font-mono text-amber-950 shadow-sm tabular-nums">
                          {(row.public_password || '').slice(0, 4)}
                        </code>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 shrink-0 border-amber-300 bg-amber-50/80 text-xs"
                          onClick={() => {
                            void navigator.clipboard.writeText(String(row.public_password).slice(0, 4));
                            toast.success('Senha copiada');
                          }}
                        >
                          <Copy className="mr-1 h-3 w-3" /> Copiar
                        </Button>
                      </div>
                    ) : null}
                    <code className="mt-2 block text-[11px] text-muted-foreground break-all">
                      SHA-256: {row.hash_sha256}
                    </code>
                    <p className="mt-1 text-[10px] text-muted-foreground leading-snug">
                      O hash identifica o conteúdo do ficheiro, não o título. Duas evidências com o mesmo SHA-256 referem o mesmo binário (ou deduplicação).
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDetail(row.id)}>
                      Detalhe
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pdfLoadingId === row.id}
                      onClick={() => downloadPdf(row.id)}
                    >
                      {pdfLoadingId === row.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button variant="destructive" size="sm" disabled={deletingId === row.id} onClick={() => onDelete(row.id)}>
                      {deletingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailData?.titulo ?? 'Detalhe'}</DialogTitle>
            <DialogDescription>
              Partilhe o link abaixo. O destinatário precisa da senha de verificação (4 caracteres hex do início do SHA-256).
            </DialogDescription>
          </DialogHeader>
          {detailData && (
            <div className="space-y-3 text-sm">
              {detailData.public_password ? (
                <div className="rounded-lg border-2 border-amber-600 bg-amber-50 p-4 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/90">Senha de verificação pública</p>
                  <p className="mt-2 flex flex-wrap items-center gap-2">
                    <code className="text-2xl font-bold tracking-[0.2em] rounded-md bg-white px-3 py-2 font-mono text-amber-950 ring-1 ring-amber-200 tabular-nums">
                      {(detailData.public_password || '').slice(0, 4)}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-amber-400 bg-white"
                      onClick={() => {
                        void navigator.clipboard.writeText(String(detailData.public_password).slice(0, 4));
                        toast.success('Senha copiada');
                      }}
                    >
                      <Copy className="mr-1 h-4 w-4" /> Copiar senha
                    </Button>
                  </p>
                  <p className="mt-2 text-xs text-amber-900/80 leading-relaxed">
                    Guarde esta senha junto ao link — sem ela, a página pública não mostra os detalhes da evidência.
                  </p>
                </div>
              ) : null}
              <p>
                <span className="text-muted-foreground">Tipo:</span> {detailData.tipo}
              </p>
              {detailData.descricao ? (
                <p>
                  <span className="text-muted-foreground">Descrição:</span> {detailData.descricao}
                </p>
              ) : null}
              <div>
                <span className="text-muted-foreground">Verificação pública:</span>
                <div className="flex gap-2 mt-1 flex-wrap items-center">
                  <code className="text-xs bg-muted px-2 py-1 rounded break-all">{verifyUrlFor(detailData.id)}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      void navigator.clipboard.writeText(verifyUrlFor(detailData.id));
                      toast.success('Link copiado');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" asChild>
                    <a href={verifyUrlFor(detailData.id)} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4 mr-1" /> Abrir
                    </a>
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                API base (diagnóstico): <code>{getApiUrl() || '(mesma origem)'}</code>
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
